// Where the calendar should open, expressed as a URL.
//
// Two things link into the calendar. A scheduling task hands over a date and
// wants the day it is discussing. The wellness list hands over its own place
// so the doctor can look at a patient's chart and come back to the month they
// were reading — the list only exists in the month view, so returning without
// the view would lose it.

export type CalendarView = 'list' | 'day' | 'week' | 'month';

const VIEWS: CalendarView[] = ['list', 'day', 'week', 'month'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface CalendarLocation {
  date?: string;
  view?: CalendarView;
  provider?: string;
}

/**
 * What a calendar link is asking for. Anything unrecognised is dropped rather
 * than guessed at — a bad date should land on today, not on 1970.
 *
 * A date with no view means the day view, which is the promise "Open in
 * calendar" on a scheduling task has always made. An explicit view wins.
 */
export function readCalendarLocation(search: string): CalendarLocation {
  const params = new URLSearchParams(search);
  const location: CalendarLocation = {};

  const date = params.get('date');
  if (date && ISO_DATE.test(date) && !Number.isNaN(new Date(`${date}T00:00:00`).getTime())) {
    location.date = date;
  }

  const view = params.get('view');
  if (view && (VIEWS as string[]).includes(view)) {
    location.view = view as CalendarView;
  } else if (location.date) {
    location.view = 'day';
  }

  const provider = params.get('provider');
  if (provider) location.provider = provider;

  return location;
}

/** The URL that reopens this spot — what "Back to calendar" points at. */
export function buildCalendarReturnTo(location: CalendarLocation): string {
  const params = new URLSearchParams();
  if (location.date) params.set('date', location.date);
  if (location.view) params.set('view', location.view);
  if (location.provider) params.set('provider', location.provider);
  const query = params.toString();
  return query ? `/calendar?${query}` : '/calendar';
}
